/**
 * Hook utilitaire pour les mutations avec gestion d'erreur
 * Affiche automatiquement les erreurs via toast
 */

import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { useToast } from '@/components/ui/toast';
import { parseApiError, logError } from '@/lib/errors';

// ========================================
// Types
// ========================================

interface MutationWithToastOptions<TData, TError, TVariables, TContext>
    extends Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'onError' | 'onSuccess'> {
    /** Nom de l'opération pour les messages (ex: "création de facture") */
    operationName: string;
    /** Message de succès (optionnel) */
    successMessage?: string;
    /** Callback en cas de succès */
    onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => void;
    /** Callback en cas d'erreur (en plus du toast) */
    onError?: (error: TError, variables: TVariables, context: TContext | undefined) => void;
    /** Clés de query à invalider en cas de succès */
    invalidateKeys?: readonly (readonly unknown[])[];
}

// ========================================
// Hook
// ========================================

/**
 * Wrapper autour de useMutation qui gère automatiquement les erreurs avec toast
 */
export function useMutationWithToast<TData, TError = Error, TVariables = void, TContext = unknown>({
    operationName,
    successMessage,
    onSuccess,
    onError,
    invalidateKeys,
    ...options
}: MutationWithToastOptions<TData, TError, TVariables, TContext>) {
    const toast = useToast();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            // Invalider les queries si spécifié
            if (invalidateKeys) {
                invalidateKeys.forEach((key) => {
                    queryClient.invalidateQueries({ queryKey: key as unknown[] });
                });
            }

            // Message de succès
            if (successMessage) {
                toast.success('Succès', successMessage);
            }

            // Callback personnalisé
            onSuccess?.(data, variables, context);
        },
        onError: (error, variables, context) => {
            // Parser l'erreur
            const parsed = parseApiError(error);

            // Log pour debug
            logError(operationName, error);

            // Afficher le toast
            toast.error(parsed.title, parsed.message);

            // Callback personnalisé
            onError?.(error, variables, context);
        },
    });
}

export default useMutationWithToast;
